export type ActivityLevel = 'high' | 'medium' | 'low';

/**
 * Calculate activity level based on action count in last 30 days
 * High: 50+ actions
 * Medium: 10-49 actions
 * Low: <10 actions
 */
export function getActivityLevel(actionsLast30Days: number): ActivityLevel {
  if (actionsLast30Days >= 50) return 'high';
  if (actionsLast30Days >= 10) return 'medium';
  return 'low';
}

/**
 * Calculate account age from createdAt date
 * Returns formatted string like "2 years 3 months" or "6 months"
 */
export function getAccountAge(createdAt: string): { years: number; months: number; days: number; formatted: string } {
  const now = new Date();
  const created = new Date(createdAt);

  // Check for invalid date
  if (isNaN(created.getTime())) {
    return { years: 0, months: 0, days: 0, formatted: 'N/A' };
  }

  let years = now.getFullYear() - created.getFullYear();
  let months = now.getMonth() - created.getMonth();
  let days = now.getDate() - created.getDate();

  // Adjust days
  if (days < 0) {
    months--;
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }

  // Adjust months
  if (months < 0) {
    years--;
    months += 12;
  }

  let formatted: string;
  if (years === 0 && months === 0) {
    formatted = days <= 1 ? '< 1 month' : `${days} days`;
  } else if (years === 0) {
    formatted = `${months} month${months !== 1 ? 's' : ''}`;
  } else if (years === 1) {
    formatted = months > 0 ? `${years} year ${months} mo` : `${years} year`;
  } else {
    formatted = months > 0 ? `${years} years ${months} mo` : `${years} years`;
  }

  return { years, months, days, formatted };
}

/**
 * Get initials from a name (first letter of first and last word)
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

